import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Fragment } from "react";
import { toast } from "sonner";
import { api, authedFileUrl, errMsg } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { ChevronLeft, Download, Mail, Printer } from "lucide-react";

export default function PayStatementDetail() {
  const { id } = useParams();
  const [stmt, setStmt] = useState(null);
  const [settings, setSettings] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get(`/pay-statements/${id}`).then((r) => setStmt(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [id]);

  if (!stmt || !settings) return <div className="text-slate-500">Loading...</div>;

  const showGross = stmt.driver_pay_type === "per_percentage";
  const logoUrl = settings.logo_path ? authedFileUrl("/settings/logo") : null;
  const status = stmt.status || "draft";

  const setStatus = async (next) => {
    try {
      const r = await api.patch(`/pay-statements/${id}/status`, { status: next });
      setStmt(r.data);
      toast.success(next === "paid" ? "Marked PAID" : "Reverted to DRAFT");
    } catch (e) { toast.error("Failed"); }
  };

  const sendEmail = async () => {
    try {
      await api.post(`/pay-statements/${id}/email`, { to_email: email });
      toast.success("Email sent");
      setEmailOpen(false);
    } catch (e) { toast.error(errMsg(e, "Email failed")); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <Link to="/pay-statements" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
          <ChevronLeft size={16} /> Back to statements
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-md border border-slate-300 bg-white overflow-hidden" data-testid="status-toggle">
            <button type="button" onClick={() => status !== "draft" && setStatus("draft")} data-testid="status-draft-btn"
              className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                status === "draft" ? "bg-amber-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              Draft
            </button>
            <button type="button" onClick={() => status !== "paid" && setStatus("paid")} data-testid="status-paid-btn"
              className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                status === "paid" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              Paid
            </button>
          </div>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-btn">
            <Printer size={14} className="mr-1" /> Print
          </Button>
          <a href={authedFileUrl(`/pay-statements/${id}/pdf`)} target="_blank" rel="noreferrer" data-testid="pdf-btn">
            <Button variant="outline"><Download size={14} className="mr-1" /> PDF</Button>
          </a>
          <a href={authedFileUrl(`/pay-statements/${id}/quickbooks.csv`)} data-testid="qb-single-btn">
            <Button variant="outline"><Download size={14} className="mr-1" /> QuickBooks</Button>
          </a>
          <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="email-btn">
                <Mail size={14} className="mr-1" /> Email
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Email pay statement</DialogTitle></DialogHeader>
              <div>
                <Label>Recipient email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="driver@example.com" data-testid="email-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
                <Button onClick={sendEmail} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-email-btn">Send</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 statement-print">
        <div className="p-8">
          <div className="flex items-start justify-between pb-5 border-b-2 border-slate-900">
            <div className="flex items-start gap-4">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="w-20 h-20 object-contain" />
              ) : (
                <div className="w-20 h-20 bg-slate-900 text-white rounded grid place-items-center font-bold">{(settings.company_name || "").trim().slice(0,2).toUpperCase() || "·"}</div>
              )}
              <div>
                <h2 className="font-bold text-xl tracking-tight">{settings.company_name}</h2>
                <p className="text-sm text-slate-600 mt-1">{settings.address}</p>
                <p className="text-xs text-slate-500 font-mono">Phone: {settings.phone} · Email: {settings.email}</p>
              </div>
            </div>
            <div className="text-right">
              <h1 className="text-2xl font-semibold tracking-tight">PAY STATEMENT</h1>
              <p className="text-xs font-mono text-slate-500 mt-2">Statement #: <strong className="text-slate-900">{stmt.statement_number}</strong></p>
              <p className="text-xs font-mono text-slate-500">Pay Date: <strong className="text-slate-900">{stmt.pay_date || stmt.created_at.slice(0, 10)}</strong></p>
              <span className={`mt-2 inline-block ${status === "paid" ? "pill-green" : "pill-amber"}`} data-testid="status-badge">
                {status.toUpperCase()}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 py-5 border-b border-slate-200">
            <div>
              <div className="eyebrow mb-1">Driver</div>
              <div className="font-semibold text-base">{stmt.driver_name}</div>
              <div className="text-xs text-slate-600 font-mono">ID: {stmt.driver_display_id}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {stmt.driver_pay_type === "per_mile" ? "Pay Per Mile" : "Pay Per Percentage"} · {stmt.driver_role?.replace("_", " ")}
              </div>
            </div>
            <div>
              <div className="eyebrow mb-1">Pay Period</div>
              <div className="font-semibold text-base">{stmt.period_start} → {stmt.period_end}</div>
            </div>
          </div>

          {(() => {
            const totalPaid = stmt.lines.reduce((s, l) => s + (parseFloat(l.paid_miles) || 0), 0);
            const totalEff = stmt.lines.reduce((s, l) => s + (parseFloat(l.effective_miles) || 0), 0);
            const hasTeam = stmt.lines.some((l) => l.is_team);
            const soloMiles = stmt.lines.filter((l) => !l.is_team).reduce((s, l) => s + (parseFloat(l.paid_miles) || 0), 0);
            const teamMiles = stmt.lines.filter((l) => l.is_team).reduce((s, l) => s + (parseFloat(l.paid_miles) || 0), 0);
            const teamLine = stmt.lines.find((l) => l.is_team);
            const refLine = teamLine || stmt.lines[0];
            const rate = Number(
              refLine && refLine.rate_single != null ? refLine.rate_single : (stmt.driver_pay_rate || 0)
            );
            const isMile = stmt.driver_pay_type === "per_mile";
            return (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 py-5 border-b border-slate-200" data-testid="rate-summary">
                <div>
                  <div className="eyebrow">Total Paid Miles</div>
                  <div className="font-mono font-semibold text-lg">{totalPaid.toFixed(1)}</div>
                  {hasTeam && (
                    <div className="text-[11px] text-slate-500">
                      Solo {soloMiles.toFixed(1)} · Team {teamMiles.toFixed(1)}
                    </div>
                  )}
                </div>
                <div>
                  <div className="eyebrow">Miles on This Driver</div>
                  <div className="font-mono font-semibold text-lg">{totalEff.toFixed(1)}</div>
                  {hasTeam && (
                    <div className="text-[11px] text-slate-500">Full miles — team drivers run the whole load</div>
                  )}
                </div>
                <div>
                  <div className="eyebrow">Rate / Driver</div>
                  <div className="font-mono font-semibold text-lg">
                    {isMile ? `$${rate.toFixed(2)} / mi` : `${rate.toFixed(1)}%`}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {hasTeam ? "Rate paid to this driver (team partner earns the same)" : "What this driver earns per mile"}
                  </div>
                </div>
                <div>
                  <div className="eyebrow">This Driver's Pay</div>
                  <div className="font-mono font-semibold text-lg">
                    ${stmt.lines.reduce((s, l) => s + (Number(l.gross_pay) || 0), 0).toFixed(2)}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {hasTeam
                      ? "miles × rate — NO halving. Full load pays 2× this."
                      : "Single driver — full load pay"}
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="py-5">
            <div className="eyebrow mb-2">Trips</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-900 text-white text-[11px]">
                  <th className="text-left px-3 py-2 font-mono">TRIP #</th>
                  <th className="text-left px-3 py-2 font-mono">ORIGIN</th>
                  <th className="text-left px-3 py-2 font-mono">DESTINATION</th>
                  <th className="text-right px-3 py-2 font-mono">MILES</th>
                  {showGross && <th className="text-right px-3 py-2 font-mono">GROSS</th>}
                  <th className="text-right px-3 py-2 font-mono">PAY</th>
                </tr>
              </thead>
              <tbody>
                {stmt.lines.map((line) => {
                  const isMile = stmt.driver_pay_type === "per_mile";
                  const miles = Number(line.effective_miles || line.paid_miles || 0);
                  const loadRate = Number(line.rate_single != null ? line.rate_single
                                      : (stmt.driver_pay_rate || 0));
                  const driverPay = Number(line.gross_pay || 0);
                  // Each driver earns miles x lead_rate directly. For team trips
                  // the full load pays 2x the driver pay.
                  const loadTotal = line.is_team ? driverPay * 2
                                   : (isMile ? miles * loadRate
                                             : Number(line.gross_amount || 0) * (loadRate / 100));
                  return (
                  <Fragment key={line.trip_id}>
                    <tr className="border-b border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs font-semibold">
                        {line.trip_number} {line.is_team && <span className="text-amber-600">(Team)</span>}
                      </td>
                      <td className="px-3 py-2 text-sm">{line.origin}</td>
                      <td className="px-3 py-2 text-sm">{line.destination}</td>
                      <td className="px-3 py-2 text-right font-mono">{miles.toFixed(1)}</td>
                      {showGross && <td className="px-3 py-2 text-right font-mono">${loadTotal.toFixed(2)}</td>}
                      <td className="px-3 py-2 text-right font-mono font-semibold">${driverPay.toFixed(2)}</td>
                    </tr>
                    <tr className="border-b border-slate-50 text-[11px] text-slate-500 italic">
                      <td colSpan={showGross ? 6 : 5} className="px-3 py-1 pl-8">
                        {isMile ? (
                          <>{miles.toFixed(1)} mi × ${loadRate.toFixed(2)}/mi = <span className="font-mono text-slate-700 font-semibold">${driverPay.toFixed(2)}</span>{line.is_team ? " (this driver's pay)" : " driver pay"}</>
                        ) : line.base_rate_per_mile ? (
                          <>
                            {miles.toFixed(1)} mi × ${Number(line.base_rate_per_mile).toFixed(2)}/mi ={" "}
                            <span className="font-mono text-slate-700">${Number(line.line_haul || 0).toFixed(2)}</span> line haul
                            {" · × "}{Math.max(0, 100 - Number(line.back_office_percent != null ? line.back_office_percent : (100 - Number(line.driver_pct || 0)))).toFixed(0)}%{" "}
                            <span className="text-slate-400">(back office {Number(line.back_office_percent != null ? line.back_office_percent : (100 - Number(line.driver_pct || 0))).toFixed(0)}%)</span>
                            {" = "}<span className="font-mono text-slate-700">${Number(line.after_back_office != null ? line.after_back_office : (line.driver_share_of_line_haul || 0)).toFixed(2)}</span>
                            {Number(line.fuel_dollars || 0) > 0 && (
                              Number(line.fuel_surcharge_rate || 0) > 0 ? (
                                <> &nbsp;+&nbsp; fuel {miles.toFixed(1)} mi × {(Number(line.fuel_surcharge_rate) * 100).toFixed(0)}% = <span className="font-mono text-emerald-600">${Number(line.fuel_dollars).toFixed(2)}</span></>
                              ) : (
                                <> &nbsp;+&nbsp; fuel surcharge <span className="font-mono text-emerald-600">${Number(line.fuel_dollars).toFixed(2)}</span></>
                              )
                            )}
                            {" = "}<span className="font-mono text-slate-700">${Number(line.subtotal_before_team_split || 0).toFixed(2)}</span>
                            {" · × "}{Number(line.driver_pct || 0).toFixed(0)}% driver{" = "}
                            <span className="font-mono text-slate-700 font-semibold">${Number(line.driver_cut_before_team != null ? line.driver_cut_before_team : (line.team_split_applied ? driverPay * 2 : driverPay)).toFixed(2)}</span>
                            {line.team_split_applied && (
                              <> &nbsp;÷ 2 (team) &nbsp;=&nbsp; <span className="font-mono text-slate-700 font-semibold">${driverPay.toFixed(2)}</span></>
                            )}
                          </>
                        ) : (
                          <>${Number(line.gross_amount || 0).toFixed(2)} × {loadRate.toFixed(2)}% = <span className="font-mono text-slate-700 font-semibold">${driverPay.toFixed(2)}</span>{line.is_team ? " (this driver's pay)" : " driver pay"}</>
                        )}
                        {line.is_team && isMile && (
                          <> &nbsp;·&nbsp; full load pay = ${loadTotal.toFixed(2)} (team partner earns the same ${driverPay.toFixed(2)})</>
                        )}
                      </td>
                    </tr>
                    {line.items.map((it, i) => {
                      const amt = Number(it.amount || 0);
                      const pct = Number(it.percent != null ? it.percent : 100);
                      const applied = Number(it.applied != null ? it.applied : amt * pct / 100);
                      return (
                        <tr key={`${line.trip_id}-${i}`} className="border-b border-slate-50 text-xs text-slate-600">
                          <td colSpan={showGross ? 5 : 4} className="px-3 py-1 pl-8">
                            <span className={it.kind === "deduction" ? "text-rose-600" : "text-emerald-600"}>
                              {it.kind === "deduction" ? "−" : "+"}
                            </span>{" "}
                            {it.label} {it.note && <span className="text-slate-400">— {it.note}</span>}
                            {pct !== 100 && <span className="text-slate-400"> (${amt.toFixed(2)} × {pct}%)</span>}
                          </td>
                          <td className={`px-3 py-1 text-right font-mono ${it.kind === "deduction" ? "text-rose-600" : "text-emerald-600"}`}>
                            {it.kind === "deduction" ? "−" : "+"}${applied.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {stmt.weekly_deductions && stmt.weekly_deductions.length > 0 && (
            <div className="mt-6" data-testid="weekly-deductions-block">
              <h3 className="font-bold text-sm text-slate-900 uppercase tracking-wide mb-2">Weekly Deductions / Reimbursements</h3>
              <table className="w-full text-sm border border-slate-200 rounded">
                <tbody>
                  {stmt.weekly_deductions.map((w, i) => {
                    const amt = Number(w.applied != null ? w.applied : w.amount || 0);
                    const isDed = w.kind === "deduction";
                    return (
                      <tr key={`${w.label || "line"}-${w.kind || "k"}-${i}`} className="border-b last:border-b-0 border-slate-100">
                        <td className={`px-3 py-2 ${isDed ? "text-rose-600" : "text-emerald-600"}`}>
                          <span className="font-semibold">{isDed ? "Deduction" : "Reimbursement"}</span>{"  "}
                          <span className="text-slate-700">{w.label}</span>
                        </td>
                        <td className={`px-3 py-2 text-right font-mono ${isDed ? "text-rose-600" : "text-emerald-600"}`}>
                          {isDed ? "−" : "+"}${amt.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="border-t-2 border-slate-900 pt-4 mt-6">
            {stmt.message && !(stmt.message_audience === "team" && !stmt.lines?.some((l) => l.is_team)) && (
              <div className="mb-4 rounded border-l-4 border-amber-500 bg-amber-50 p-3" data-testid="statement-message">
                <div className="eyebrow text-amber-900 mb-1">
                  Message from Dispatch
                  {stmt.message_audience === "all" && <span className="ml-2 text-[10px] font-semibold text-amber-700">· TO ALL DRIVERS</span>}
                  {stmt.message_audience === "team" && <span className="ml-2 text-[10px] font-semibold text-amber-700">· TO TEAM DRIVERS</span>}
                </div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{stmt.message}</div>
              </div>
            )}
            <div className="ml-auto max-w-xs space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Gross Pay</span><span className="font-mono font-semibold">${stmt.totals.gross.toFixed(2)}</span></div>
              <div className="flex justify-between text-rose-600"><span>Deductions</span><span className="font-mono">−${stmt.totals.deductions.toFixed(2)}</span></div>
              <div className="flex justify-between text-emerald-600"><span>Reimbursements</span><span className="font-mono">+${stmt.totals.reimbursements.toFixed(2)}</span></div>
              <div className="flex justify-between pt-2 border-t border-slate-200">
                <span className="font-bold text-base">NET PAY</span>
                <span className="font-mono font-bold text-base text-blue-700" data-testid="net-pay">${stmt.totals.net.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-8 text-center">
            {settings.company_name} &middot; {settings.address} &middot; {settings.phone} &middot; {settings.email}
          </p>
        </div>
      </div>
    </div>
  );
}
